import {
  AnalyticsRecord,
  KPIData,
  TrendData,
  FieldChartData,
  ActionChartData,
  HeatMapData,
  KPISection,
  SummaryRow,
  DistributionData,
  DayKey,
} from './types';

const toNumber = (value: any): number => Number(value ?? 0);

export const normalizeAnalyticsData = (
  response: any[]
): AnalyticsRecord[] => {
  if (!Array.isArray(response)) return [];
  //console.log("response in normalise",response)
  const records: AnalyticsRecord[] = [];

  response.forEach(group => {
    if (!Array.isArray(group)) return;

    group.forEach(item => {
      // Total row doesn't have Field Name.
      const fieldName =
        item['Field Name'] ||
        group.find((x: any) => x['Field Name'])?.['Field Name'] ||
        'Unknown';

      records.push({
        fieldName,

        action: item.action_name,

        t0: toNumber(item.t0),
        t1: toNumber(item.t1),
        t2: toNumber(item.t2),
        t3: toNumber(item.t3),
        t4: toNumber(item.t4),
        t5: toNumber(item.t5),
        t6: toNumber(item.t6),

        week: toNumber(item['week avg']),
        month: toNumber(item['month avg']),
      });
    });
  });
  //console.log("records in normalize",records)
  return records;
};

export const buildKPIData = (
  records: AnalyticsRecord[]
): KPIData => {
  const calculate = (period: 'week' | 'month'): KPISection => {
    const getValue = (r: AnalyticsRecord) =>
      period === 'week' ? r.week : r.month;

    return {
      total: records
        .filter(r => r.action === 'Total')
        .reduce((sum, r) => sum + getValue(r), 0),

      added: records
        .filter(r => r.action === 'Added')
        .reduce((sum, r) => sum + getValue(r), 0),

      modified: records
        .filter(r => r.action === 'Modified')
        .reduce((sum, r) => sum + getValue(r), 0),

      deleted: records
        .filter(r => r.action === 'Deleted')
        .reduce((sum, r) => sum + getValue(r), 0),
    };
  };

  return {
    week: calculate('week'),
    month: calculate('month'),
  };
};

export const buildTrendData = (
  records: AnalyticsRecord[]
): TrendData[] => {

  const added = records.filter(r => r.action === 'Added');

  const modified = records.filter(r => r.action === 'Modified');

  const deleted = records.filter(r => r.action === 'Deleted');

  const sum = (
    rows: AnalyticsRecord[],
    key: keyof AnalyticsRecord
  ) =>
    rows.reduce((total, row) => total + Number(row[key]), 0);

  return [
    {
      day: 'T0',
      added: sum(added, 't0'),
      modified: sum(modified, 't0'),
      deleted: sum(deleted, 't0'),
    },
    {
      day: 'T1',
      added: sum(added, 't1'),
      modified: sum(modified, 't1'),
      deleted: sum(deleted, 't1'),
    },
    {
      day: 'T2',
      added: sum(added, 't2'),
      modified: sum(modified, 't2'),
      deleted: sum(deleted, 't2'),
    },
    {
      day: 'T3',
      added: sum(added, 't3'),
      modified: sum(modified, 't3'),
      deleted: sum(deleted, 't3'),
    },
    {
      day: 'T4',
      added: sum(added, 't4'),
      modified: sum(modified, 't4'),
      deleted: sum(deleted, 't4'),
    },
    {
      day: 'T5',
      added: sum(added, 't5'),
      modified: sum(modified, 't5'),
      deleted: sum(deleted, 't5'),
    },
    {
      day: 'T6',
      added: sum(added, 't6'),
      modified: sum(modified, 't6'),
      deleted: sum(deleted, 't6'),
    },
  ];
};

export const buildFieldChart = (
  records: AnalyticsRecord[],
  period: 'week' | 'month'
): FieldChartData[] => {

  const grouped: Record<string, FieldChartData> = {};

  records.forEach(record => {

    if (!grouped[record.fieldName]) {
      grouped[record.fieldName] = {
        fieldName: record.fieldName,
        value: 0,
        added: 0,
        modified: 0,
        deleted: 0,
      };
    }

    const value =
      period === 'week'
        ? record.week
        : record.month;

    switch (record.action) {

      case 'Added':
        grouped[record.fieldName].added = value;
        break;

      case 'Modified':
        grouped[record.fieldName].modified = value;
        break;

      case 'Deleted':
        grouped[record.fieldName].deleted = value;
        break;

      case 'Total':
        grouped[record.fieldName].value = value;
        break;
    }

  });

    // Calculate total when API doesn't provide one
  Object.values(grouped).forEach(item => {
    if (item.value === 0) {
      item.value =
        item.added +
        item.modified +
        item.deleted;
    }
  });


  return Object.values(grouped)
    .sort((a, b) => b.value - a.value);
};

export const buildActionChart = (
  records: AnalyticsRecord[],
  period: 'week' | 'month'
): ActionChartData[] => {

  const getValue = (record: AnalyticsRecord) => {

    if (period === 'month') {
      return record.month;
    }

    // Week = sum of T0-T6
    return (
      record.t0 +
      record.t1 +
      record.t2 +
      record.t3 +
      record.t4 +
      record.t5 +
      record.t6
    );
  };

  return [
    {
      name: 'Added',
      value: records
        .filter(r => r.action === 'Added')
        .reduce((sum, r) => sum + getValue(r), 0),
    },
    {
      name: 'Modified',
      value: records
        .filter(r => r.action === 'Modified')
        .reduce((sum, r) => sum + getValue(r), 0),
    },
    {
      name: 'Deleted',
      value: records
        .filter(r => r.action === 'Deleted')
        .reduce((sum, r) => sum + getValue(r), 0),
    },
  ];
};

export const buildHeatMapData = (
  records: AnalyticsRecord[]
): HeatMapData[] => {

  const grouped: Record<string, HeatMapData> = {};
//console.log("records: [build heatmap]", records)
  records.forEach(record => {

    if (!grouped[record.fieldName]) {

      grouped[record.fieldName] = {
        fieldName: record.fieldName,

        t0: 0,
        t1: 0,
        t2: 0,
        t3: 0,
        t4: 0,
        t5: 0,
        t6: 0,

        added: 0,
        modified: 0,
        deleted: 0,
        total: 0,

        week: {
          t0: { added: 0, modified: 0, deleted: 0, total: 0 },
          t1: { added: 0, modified: 0, deleted: 0, total: 0 },
          t2: { added: 0, modified: 0, deleted: 0, total: 0 },
          t3: { added: 0, modified: 0, deleted: 0, total: 0 },
          t4: { added: 0, modified: 0, deleted: 0, total: 0 },
          t5: { added: 0, modified: 0, deleted: 0, total: 0 },
          t6: { added: 0, modified: 0, deleted: 0, total: 0 },
        },
      };
    }

    const row = grouped[record.fieldName];

    const action = record.action.toLowerCase();

    const days = [
      record.t0,
      record.t1,
      record.t2,
      record.t3,
      record.t4,
      record.t5,
      record.t6,
    ];
    if (action === "total") {
  return;
}
    days.forEach((value, index) => {

      const dayKey = `t${index}` as keyof typeof row.week;

      row[dayKey] += value;

 if (action === "added") {
    row.week[dayKey].added += value;
}

if (action === "modified") {
    row.week[dayKey].modified += value;
}

if (action === "deleted") {
    row.week[dayKey].deleted += value;
}

// calculate total from actions only
row.week[dayKey].total =
    row.week[dayKey].added +
    row.week[dayKey].modified +
    row.week[dayKey].deleted;

    });

    if (action === "added") {
    row.added += record.month;
}

if (action === "modified") {
    row.modified += record.month;
}

if (action === "deleted") {
    row.deleted += record.month;
}

row.total =
    row.added +
    row.modified +
    row.deleted;

  });

 /*  Object.values(grouped).forEach(row => {
    row.total =
      row.added +
      row.modified +
      row.deleted;
  }); */

  return Object.values(grouped).sort(
    (a, b) => b.total - a.total
  );
};

export const buildSummary = (
  records: AnalyticsRecord[]
): SummaryRow[] => {

  const grouped: Record<string, SummaryRow> = {};

  records.forEach(record => {

    if (!grouped[record.fieldName]) {

      grouped[record.fieldName] = {
        fieldName: record.fieldName,
        rows: [
          {
            action: "Added",
            t0: 0,
            t1: 0,
            t2: 0,
            t3: 0,
            t4: 0,
            t5: 0,
            t6: 0,
            week: 0,
            month: 0,
          },
          {
            action: "Modified",
            t0: 0,
            t1: 0,
            t2: 0,
            t3: 0,
            t4: 0,
            t5: 0,
            t6: 0,
            week: 0,
            month: 0,
          },
          {
            action: "Deleted",
            t0: 0,
            t1: 0,
            t2: 0,
            t3: 0,
            t4: 0,
            t5: 0,
            t6: 0,
            week: 0,
            month: 0,
          },
          {
            action: "Total",
            t0: 0,
            t1: 0,
            t2: 0,
            t3: 0,
            t4: 0,
            t5: 0,
            t6: 0,
            week: 0,
            month: 0,
          },
        ],
      };
    }

    const field = grouped[record.fieldName];

    const actionRow = field.rows.find(
      r => r.action === record.action
    );

    if (!actionRow) return;

    actionRow.t0 = record.t0;
    actionRow.t1 = record.t1;
    actionRow.t2 = record.t2;
    actionRow.t3 = record.t3;
    actionRow.t4 = record.t4;
    actionRow.t5 = record.t5;
    actionRow.t6 = record.t6;

    actionRow.week = record.week;
    actionRow.month = record.month;

  });

  /**
   * Build Total Row
   */

  Object.values(grouped).forEach(field => {

    const added =
      field.rows.find(r => r.action === "Added")!;

    const modified =
      field.rows.find(r => r.action === "Modified")!;

    const deleted =
      field.rows.find(r => r.action === "Deleted")!;

    const total =
      field.rows.find(r => r.action === "Total")!;

    total.t0 = added.t0 + modified.t0 + deleted.t0;
    total.t1 = added.t1 + modified.t1 + deleted.t1;
    total.t2 = added.t2 + modified.t2 + deleted.t2;
    total.t3 = added.t3 + modified.t3 + deleted.t3;
    total.t4 = added.t4 + modified.t4 + deleted.t4;
    total.t5 = added.t5 + modified.t5 + deleted.t5;
    total.t6 = added.t6 + modified.t6 + deleted.t6;

    total.week =
      added.week +
      modified.week +
      deleted.week;

    total.month =
      added.month +
      modified.month +
      deleted.month;

  });

  return Object.values(grouped).sort(
    (a, b) =>
      b.rows[3].month -
      a.rows[3].month
  );

};

export const buildDistributionData = (
  records: AnalyticsRecord[],
  period: 'week' | 'month'
) => {
  const grouped: Record<
    string,
    {
      fieldName: string;
      Added: number;
      Modified: number;
      Deleted: number;
      AddedValue: number;
      ModifiedValue: number;
      DeletedValue: number;
      TotalValue: number;
    }
  > = {};

  records.forEach(record => {
    if (!grouped[record.fieldName]) {
      grouped[record.fieldName] = {
        fieldName: record.fieldName,
        Added: 0,
        Modified: 0,
        Deleted: 0,

        AddedValue: 0,
        ModifiedValue: 0,
        DeletedValue: 0,

        TotalValue: 0,
      };
    }

    const value =
      period === 'week'
        ? record.week
        : record.month;

    switch (record.action) {
      case 'Added':
        grouped[record.fieldName].AddedValue = value;
        break;

      case 'Modified':
        grouped[record.fieldName].ModifiedValue = value;
        break;

      case 'Deleted':
        grouped[record.fieldName].DeletedValue = value;
        break;

      case 'Total':
        grouped[record.fieldName].TotalValue = value;
        break;
    }
  });

  return Object.values(grouped).map(item => {
    const total =
      item.TotalValue ||
      item.AddedValue +
        item.ModifiedValue +
        item.DeletedValue;

    return {
      ...item,

      TotalValue: total,

      Added: total
        ? (item.AddedValue / total) * 100
        : 0,

      Modified: total
        ? (item.ModifiedValue / total) * 100
        : 0,

      Deleted: total
        ? (item.DeletedValue / total) * 100
        : 0,
    };
  });
};

export const formatCompact = (value: number) =>
  Intl.NumberFormat('en', {
    notation: 'compact',
    maximumFractionDigits: 1,
  }).format(value);